import { Component } from 'react';
import classNames from 'classnames/bind';
import { Dropdown } from 'react-bootstrap';
import autoBind from 'react-autobind';

import { checkAllAssetsStatus, watchlistTopicsQueryCreator } from '../../../helpers/helpers';
import { pickParams } from '../../../helpers/commonHelpers';
import withComponentName from '../../../decorators/withComponentName';

import Styles from '../story-filters-styles.module.scss';
import { filterSelectedTimeFilter } from '../../../helpers/timeFilterHelpers';
import { filterSelectedSentimentForStories } from '../../../helpers/storyHelpers';
import { SILVER } from '../../../data/permissions';
import { refreshValuesConversion } from '../../../data/filters/storyFilters';
import RefreshEveryFilter from '../RefreshEveryFilter';
import FilterButtonInfo from '../StoryFiltersButton/FilterButtonInfo';
import FiltersMobileModal from '../FiltersMobileModal';

import StorageSvc from '../../../services/StorageSvc';

const cx = classNames.bind(Styles);

class DropdownRadioFilter extends Component {
  constructor(props) {
    super(props);
    this.state = {
      openFilterModal: false,
      dropdown: {
        open: false,
        selected: {},
        options: [],
      },
      stateChangedFromFilter: false, // to determine if state value changed from filter or outside
    };
    autoBind(this);
  }

  componentDidMount() {
    const {
      type, values, savedFilterValue,
    } = this.props;

    const presisted = StorageSvc.getItem(`${type}Filter`);

    if (presisted !== null && !savedFilterValue) {
      const rehydrate = JSON.parse(presisted);

      rehydrate.dropdown.options = values;

      if (type === 'period' && !values.some(({ value }) => value === rehydrate.dropdown.selected.value)) {
        rehydrate.dropdown.selected = values[values.length - 1];
      }

      this.setState(rehydrate);
    } else {
      this.setValues(
        values,
        savedFilterValue,
      );
    }
  }

  componentDidUpdate(prevProps) {
    const { savedFilterValue, values } = this.props;
    const { stateChangedFromFilter } = this.state;

    // only fire this when languages changed from another component
    if (savedFilterValue !== prevProps.savedFilterValue && !stateChangedFromFilter) {
      this.setValues(
        values,
        savedFilterValue,
      );
    }
  }

  componentWillUnmount() {
    const { token } = this.props;
    // save guest user filter value to localstorage
    if (!token) this.saveState();
  }

  handleCloseModal() {
    this.setState({ openFilterModal: false });
  }

  setValues(values, selectedValue) {
    const { dropdown } = this.state;

    let updatedSelectedValue = selectedValue;
    const refreshValConversions = [
      'seconds10',
      'seconds30',
      'minute1',
      'minutes10',
    ];

    if (refreshValConversions.includes(selectedValue)) {
      updatedSelectedValue = refreshValuesConversion[selectedValue];
    }
    this.setState({
      dropdown: {
        ...dropdown,
        options: values,
      },
    }, () => {
      this.setInitialSelection(updatedSelectedValue);
    });
  }

  setInitialSelection(value) {
    const { values } = this.props;
    const { dropdown } = this.state;

    const targetedValue = value;

    for (const item of values) {
      if (item.value === targetedValue) {
        this.setState({
          dropdown: {
            ...dropdown,
            selected: item,
          },
        });
        break;
      }
    }
  }

  setValueSelected(selectedItem) {
    const {
      freezeStories, piwikEnabled, type, action, token,
    } = this.props;
    const { dropdown } = this.state;

    if (piwikEnabled && type === 'period') _paq.push(['trackEvent', 'Filters', 'Time period', selectedItem.name]);
    if (piwikEnabled && type === 'order') _paq.push(['trackEvent', 'Filters', 'Story order', selectedItem.name]);
    if (piwikEnabled && type === 'refresh') _paq.push(['trackEvent', 'Filters', 'Refresh period', selectedItem.name]);

    freezeStories(false);

    this.setState({
      dropdown: {
        ...dropdown,
        open: false,
        selected: selectedItem,
      },
      stateChangedFromFilter: true,
    }, () => {
      // save guest user filter value to localstorage
      if (!token) this.saveState();

      action(selectedItem.value, token);
      if (type === 'order' && selectedItem.value !== 'read') {
        StorageSvc.setItem(
          'selectedOrderTab',
          JSON.stringify({ activeOrderTab: selectedItem.value }),
        );
      }
      setTimeout(() => {
        this.setState({ stateChangedFromFilter: false });
      }, 500);
    });
  }

  toggleDropdown(status, event, source = {}) {
    const { dropdown } = this.state;
    const { freezeStories, width } = this.props;

    if (width <= 1024) return this.openFilterModal();

    this.setState({
      dropdown: {
        ...dropdown,
        open: status,
      },
    }, () => {
      freezeStories(status);
    });
  }

  saveState() {
    const { dropdown } = this.state;
    const { type } = this.props;

    StorageSvc.setItem(
      `${type}Filter`,
      JSON.stringify({ dropdown: { ...dropdown, open: false } }),
    );
  }

  refreshStories() {
    const {
      token,
      piwikEnabled,
      watchlistRefresh,
      storiesLoading,
      storyFilters,
      actionRefresh,
      actions,
      activeWatchlist,
      activeWatchlistTopics,
      accessLevels,
    } = this.props;

    const hasSilverAccess = accessLevels.includes(SILVER);
    // refresh stories when stories not loading
    const params = pickParams(
      { ...storyFilters },
      'categories', 'order_by', 'selected_time_filter', 'languages', 'min_score', 'translate_to',
      'blacklisted_domain_ids', 'whitelisted_domain_ids', 'domains_filtering_mode',
      ...filterSelectedTimeFilter(storyFilters.selected_time_filter),
      ...filterSelectedSentimentForStories(storyFilters, hasSilverAccess),
    );

    params.fold_similar_stories = storyFilters.group_similar_stories;
    params.translate_to = storyFilters.translate_to;

    let query;

    if (!storiesLoading) {
      actionRefresh();
    } else if (watchlistRefresh && !token) {
      actions.updateWatchlistPageBegin();

      query = watchlistTopicsQueryCreator(activeWatchlist.search_queries);

      actions.loadGuestWatchlistStories({ query, params });
      setTimeout(() => actions.fetchTrendingStoriesSuccess(), 900);
    } else if (watchlistRefresh && token) {
      params.include_all_topics = checkAllAssetsStatus(activeWatchlistTopics);

      query = watchlistTopicsQueryCreator(
        // Include no more than 33 topics
        // to not exceed URL length limit (2048 characters)
        activeWatchlistTopics.filter((item) => !item.hidden).slice(0, 33),
        token,
      );

      actions.updateWatchlistPageBegin();
      actions.getWatchlistStories({
        watchlistId: activeWatchlist.id,
        params,
        nextPage: false,
        query,
      });

      if (piwikEnabled) _paq.push(['trackEvent', 'Filters', 'Refresh newsfeed']);
    }
  }

  openFilterModal() {
    const { openFilterModal } = this.state;

    this.setState({ openFilterModal: !openFilterModal });
  }

  render() {
    const {
      rbi, type, dropdownTitle, refreshFeature, width, isSearch, isSimpleLayout,
    } = this.props;
    const { dropdown, openFilterModal } = this.state;
    const refreshEveryClass = cx({
      'refresh-btn-wrapper': type === 'refresh',
    });
    const disabled = isSearch && type === 'order';
    return (
      <Dropdown
        bsPrefix={cx('story-filters_btn', { refreshEveryClass })}
        onToggle={(status, event, source) => this.toggleDropdown(status, event, source)}
        show={dropdown.open}
        className={cx(isSearch ? ['search-disabled', 'order-select'] : '', { disabled: isSimpleLayout && type !== 'refresh' })}
      >
        {disabled && (
          <span className="score-popover">
            Results sorted by search relevance. This filter is ignored.
          </span>
        )}
        <Dropdown.Toggle
          id={cx(`${type}-dropdown`)}
          className={cx(disabled ? 'search-disabled' : '')}
          variant="default"
        >
          <FilterButtonInfo
            dropdownTitle={dropdownTitle}
            openFilter={dropdown.open}
            rbi={rbi}
            width={width}
            selectedValue={dropdown.selected.name}
            buttonErr={dropdown.selected === 0}
          />
        </Dropdown.Toggle>
        {width >= 1025 ? (
          <Dropdown.Menu
            rootCloseEvent="click"
            show={dropdown.open}
            flip={false}
            bsPrefix="dropdown-menu"
          >
            <FilterInner
              dropdown={dropdown}
              dropdownTitle={dropdownTitle}
              setValueSelected={this.setValueSelected}
              type={type}
              handleCloseModal={this.handleCloseModal}
              refreshFeature={refreshFeature}
              isSearch={isSearch}
            />
          </Dropdown.Menu>
        ) : (
          <FiltersMobileModal
            openFilter={openFilterModal}
            handleCloseModal={this.handleCloseModal}
            classname={cx(type, { disabledMessage: !!isSimpleLayout })}
            showCloseIcon
          >
            {isSimpleLayout && !disabled ? (
              <div className={cx('modalContainer')}>
                <div className={cx('modalContent')}>
                  This filter is disabled for your
                  {' '}
                  <br />
                  current layout settings
                </div>
                <button
                  type="button"
                  className={cx('modalBtn')}
                  onClick={this.handleCloseModal}
                >
                  OK
                </button>
              </div>
            ) : (
              <FilterInner
                dropdown={dropdown}
                dropdownTitle={dropdownTitle}
                setValueSelected={this.setValueSelected}
                type={type}
                handleCloseModal={this.handleCloseModal}
                refreshFeature={refreshFeature}
                isSearch={isSearch}
              />
            )}
          </FiltersMobileModal>
        )}
        {isSimpleLayout && !disabled
          && (
            <div className={cx('tooltip')}>
              This filter is disabled for your
              <br />
              current layout settings
            </div>
          )}
      </Dropdown>
    );
  }
}

const FilterInner = ({
  dropdown,
  dropdownTitle,
  setValueSelected,
  type,
  refreshFeature,
  isSearch,
}) => (
  <div
    className={cx('dropdown-items-wrapper', 'full-width-dropdown', {
      'order-search-disabled': isSearch && type === 'order',
      'dropdown-items-wrapper-order': type === 'order',
      'dropdown-items-wrapper-refresh': type === 'refresh',
    })}
  >
    <span className={cx('dropdown_title')}>
      {dropdownTitle}
      :
    </span>
    {(isSearch && type === 'order') && (
      <span className={classNames('order-mobile-tip')}>
        Results sorted by search relevance.
        <br />
        Use shorter time periods for newer content.
      </span>
    )}
    {type === 'refresh' ? (
      <>
        <RefreshEveryFilter
          dropdown={dropdown}
          setValueSelected={setValueSelected}
          refreshFeature={refreshFeature}
        />
      </>
    ) : (
      <>
        {dropdown.options.map((item, index) => (
          <Dropdown.Item
            key={item.name}
            eventKey={index}
            active={dropdown.selected.value === item.value}
            onSelect={() => setValueSelected(item)}
            disabled={isSearch}
          >
            <span className={cx('dropdown__radio_box')} />
            {item.name}
          </Dropdown.Item>
        ))}
      </>
    )}
  </div>
);

export default withComponentName(DropdownRadioFilter);
